// TickerLogo.jsx — logo with monogram fallback + the currency-aware PriceCard.

import React, { useState, useEffect, useRef } from 'react';
import { baseSymbol, fmtAssetPrice, LOGO_HOST } from './rrtvFormat';

// ---------- Monogram fallback ----------
export function TickerMonogram({ ticker, size = 20, className = '' }) {
  const letter = baseSymbol(ticker).charAt(0);
  return (
    <span className={`rrtv-logo-monogram ${className}`.trim()}
      style={{ width: size, height: size, fontSize: size * 0.45 }}>
      {letter}
    </span>
  );
}

const LOGO_MAX_RETRY = 3;

export function TickerLogo({ ticker, size = 20, className = '' }) {
  const base = baseSymbol(ticker);
  const [attempt, setAttempt] = useState(0); // 0 = first try; >0 = cache-busted retries
  const [failed,  setFailed]  = useState(false);
  const timerRef = useRef(null);

  // New symbol in this (reused) slot → clear any pending retry and start fresh.
  useEffect(() => {
    clearTimeout(timerRef.current);
    setAttempt(0);
    setFailed(false);
  }, [base]);
  useEffect(() => () => clearTimeout(timerRef.current), []);

  if (failed) return <TickerMonogram ticker={ticker} size={size ?? 20} className={className} />;

  const sizeProps = size != null ? { width: size, height: size } : {};
  // First load hits cache; retries append a param to bypass a cached/transient error.
  const src = attempt === 0 ? `${LOGO_HOST}/${base}.svg` : `${LOGO_HOST}/${base}.svg?r=${attempt}`;

  return (
    <img key={base} className={`rrtv-logo-img ${className}`.trim()}
      src={src} alt={base} {...sizeProps}
      onError={() => {
        // Don't latch on the first error — most are transient aborts/bursts.
        // Retry with backoff, fall back to the monogram only after MAX_RETRY.
        if (attempt < LOGO_MAX_RETRY) {
          clearTimeout(timerRef.current);
          timerRef.current = setTimeout(() => setAttempt(a => a + 1), 500 * (attempt + 1));
        } else {
          setFailed(true);
        }
      }} />
  );
}

// ---------- PriceCard — currency-aware + clickable (accepts spread press props) ----------
export function PriceCard({ t, size = 'small', pressProps }) {
  const up  = (t.change ?? t.var ?? 0) >= 0;
  const chg = t.change ?? t.var ?? 0;
  const prc = t.price  ?? t.ult;
  const cur = t.currency || 'R$';
  return (
    <div
      className={`rrtv-price-card ${up ? 'pos' : 'neg'} ${size}${pressProps ? ' rrtv-clickable' : ''}`}
      {...(pressProps || {})}
    >
      <TickerLogo ticker={t.ticker} size={null} className="rrtv-price-card-logo" />
      <div className="rrtv-price-top">
        <span className="rrtv-price-tk">{t.ticker}</span>
        <span className={`rrtv-price-var ${up ? 'pos' : 'neg'}`}>
          {up ? '+' : ''}{chg.toFixed(2)}%
        </span>
      </div>
      <div className="rrtv-price-value">{fmtAssetPrice(prc, cur === 'R$' ? 'BRL' : cur)}</div>
      {size === 'large' && t.name && <div className="rrtv-price-name">{t.name}</div>}
    </div>
  );
}
